import { DashboardLayout } from '@/components/DashboardLayout';
import PageTour from '@/components/PageTour';
import ShareViewButton from '@/components/common/ShareViewButton';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  BarChart3, TrendingUp, Activity, Zap, CalendarDays, FileText, CreditCard,
  Tags, ArrowDownUp, AlertTriangle, Handshake, CalendarClock, PieChart, FileSpreadsheet, Users
, FileBarChart} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useState, useEffect } from 'react';
import { useBranchStore } from '@/stores';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Building2, FileDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { exportTieOutPackToPDF } from '@/lib/pdfExport';

const reportSections = [
  {
    title: 'Financial Reports',
    titleKey: 'reports_sec_financial',
    color: 'emerald',
    items: [
      { path: '/daily-summary', icon: CalendarDays, label: 'Daily Summary', desc: 'Day-by-day sales, expenses & profit breakdown', badge: 'Popular' },
      { path: '/enhanced-pnl', icon: TrendingUp, label: 'Enhanced P&L', desc: 'Detailed profit & loss with trends' },
      { path: '/category-report', icon: Tags, label: 'Category Report', desc: 'Expenses grouped by category' },
      { path: '/credit-report', icon: CreditCard, label: 'Credit Report', desc: 'Customer credit balances & aging' },
      { path: '/supplier-report', icon: FileText, label: 'Supplier Report', desc: 'Supplier transactions & balances' },
      { path: '/bank-statements', icon: FileSpreadsheet, label: 'Bank Statements', desc: 'Upload & reconcile bank statements' },
      { path: '/bank-accounts', icon: FileSpreadsheet, label: 'Bank Accounts', desc: 'Manage company bank accounts' },
      { path: '/reconciliation', icon: ArrowDownUp, label: 'Reconciliation', desc: 'Match transactions with bank records' },
      { path: '/platform-reconciliation', icon: ArrowDownUp, label: 'Platform Reconciliation', desc: 'Track platform sales vs payouts' },
      { path: '/monthly-recon-report', icon: CalendarDays, label: 'Monthly Platform Report', desc: 'Monthly platform fee analysis & variance', badge: 'New' },
      { path: '/duplicate-report', icon: ArrowDownUp, label: 'Duplicate Report', desc: 'Find & remove duplicate entries across all modules', badge: 'New' },
    ]
  },
  {
    title: 'Analytics & Insights',
    titleKey: 'reports_sec_analytics',
    color: 'blue',
    items: [
      { path: '/analytics', icon: BarChart3, label: 'Analytics Dashboard', desc: 'Overview charts and KPIs', badge: 'Popular' },
      { path: '/advanced-analytics', icon: Zap, label: 'Advanced Analytics', desc: 'AI-powered insights & comparisons' },
      { path: '/visualizations', icon: PieChart, label: 'Visualizations', desc: 'Interactive charts and graphs' },
      { path: '/sales-forecast', icon: TrendingUp, label: 'Sales Forecast', desc: 'AI-predicted sales trends' },
      { path: '/trend-comparison', icon: TrendingUp, label: 'Trend Comparison', desc: 'Compare periods side by side' },
      { path: '/anomaly-detection', icon: AlertTriangle, label: 'Anomaly Detection', desc: 'Spot unusual patterns in data' },
    ]
  },
  {
    title: 'Operations',
    titleKey: 'reports_sec_operations',
    color: 'amber',
    items: [
      { path: '/shift-report', icon: CalendarClock, label: 'Shift Report', desc: 'Sales & activity by shift/time' },
      { path: '/performance-report', icon: Activity, label: 'Performance Report', desc: 'Branch & staff performance metrics' },
      { path: '/partner-pl-report', icon: Handshake, label: 'Partner P&L', desc: 'Profit sharing with partners' },
    ]
  },
  {
    title: 'Customer Reports',
    titleKey: 'reports_sec_customers',
    color: 'rose',
    items: [
      { path: '/customer-reports', icon: Users, label: 'Customer Reports', desc: 'Ledger, Aging, Sales-by-Customer & Top Customers — with date range & export', badge: 'New' },
      { path: '/credit-report', icon: CreditCard, label: 'Credit Report', desc: 'Customer credit balances & aging' },
    ]
  },
  {
    title: 'Tools',
    titleKey: 'reports_sec_tools',
    color: 'violet',
    items: [
      { path: '/report-builder', icon: FileText, label: 'Custom Report Builder', desc: 'Build your own reports with filters', badge: 'New' },
    ]
  },
];

const colorMap = {
  emerald: { bg: 'bg-success/10 hover:bg-success/20 border-success/30', icon: 'text-success', title: 'text-success', header: 'bg-success' },
  blue: { bg: 'bg-info/10 hover:bg-info/20 border-info/30', icon: 'text-info', title: 'text-info', header: 'bg-info' },
  amber: { bg: 'bg-warning/10 hover:bg-warning/20 border-warning/30', icon: 'text-warning', title: 'text-warning', header: 'bg-warning' },
  violet: { bg: 'bg-violet-50 hover:bg-violet-100 border-violet-200', icon: 'text-violet-600', title: 'text-violet-800', header: 'bg-violet-500' },
  rose: { bg: 'bg-rose-50 hover:bg-rose-100 border-rose-200', icon: 'text-rose-600', title: 'text-rose-800', header: 'bg-rose-500' },
};

export default function ReportsPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { branches, fetchBranches } = useBranchStore();
  // iter945 — Global Branch Context: a branch chosen here is remembered and
  // auto-applied (?branch=<id>) to every report opened from this hub, so
  // branch-aware reports (Aging, Account Statement, …) open pre-scoped.
  const [reportBranch, setReportBranch] = useState(() => {
    try { return localStorage.getItem('report_branch_ctx') || 'all'; } catch { return 'all'; }
  });
  useEffect(() => { fetchBranches?.(); }, [fetchBranches]);
  const setBranchCtx = (v) => {
    setReportBranch(v);
    try { localStorage.setItem('report_branch_ctx', v); } catch {}
  };
  const openReport = (path) => {
    if (reportBranch && reportBranch !== 'all') {
      const sep = path.includes('?') ? '&' : '?';
      navigate(`${path}${sep}branch=${reportBranch}&branch_id=${reportBranch}`);
    } else {
      navigate(path);
    }
  };

  // iter951 — Accountant's Tie-Out Pack (Balance Sheet + Trial Balance + Account Summary in one PDF)
  const [packPeriod, setPackPeriod] = useState('this_month');
  const [packLoading, setPackLoading] = useState(false);
  const _packRange = (key) => {
    const now = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    const y = now.getFullYear(), m = now.getMonth();
    if (key === 'this_month') return { start: iso(new Date(y, m, 1)), end: iso(new Date(y, m + 1, 0)), label: now.toLocaleString(undefined, { month: 'short', year: 'numeric' }) };
    if (key === 'last_month') return { start: iso(new Date(y, m - 1, 1)), end: iso(new Date(y, m, 0)), label: new Date(y, m - 1, 1).toLocaleString(undefined, { month: 'short', year: 'numeric' }) };
    if (key === 'this_year') return { start: `${y}-01-01`, end: iso(new Date(y, 11, 31)), label: String(y) };
    return { start: null, end: iso(now), label: 'All time' };
  };
  const downloadTieOutPack = async () => {
    setPackLoading(true);
    try {
      const { start, end, label } = _packRange(packPeriod);
      const bp = (reportBranch && reportBranch !== 'all') ? `&branch_id=${reportBranch}` : '';
      const sp = start ? `start_date=${start}&` : '';
      const [tb, as_, bs] = await Promise.all([
        api.get(`/accounting/trial-balance?${sp}end_date=${end}${bp}`),
        api.get(`/accounting/account-summary?${sp}end_date=${end}${bp}`),
        api.get(`/accounting/balance-sheet?${start ? `start_date=${start}&` : ''}as_of_date=${end}${bp}`),
      ]);
      const branchName = reportBranch === 'all' ? 'All Branches' : (branches.find(b => b.id === reportBranch)?.name || '');
      exportTieOutPackToPDF({
        balanceSheet: bs.data,
        trialBalance: tb.data,
        accountSummary: as_.data,
        periodLabel: `${label}${branchName ? ` · ${branchName}` : ''}`,
      });
    } catch {
      /* toast handled in the PDF fn / api */
    }
    setPackLoading(false);
  };

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div>
          <div className="flex items-center gap-4">

            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center shadow-lg shadow-orange-200/50">

              <FileBarChart size={24} className="text-white" />

            </div>

            <div>

              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="reports-hub-title">{t('reports_title')}</h1>

              <p className="text-sm text-muted-foreground mt-0.5">{t('reports_center')}</p>

            </div>

          </div>
          <div className="flex items-center gap-2">
            <Select value={reportBranch} onValueChange={setBranchCtx}>
              <SelectTrigger className="w-48 h-9" data-testid="reports-branch-context">
                <div className="flex items-center gap-1.5"><Building2 size={14} /><SelectValue placeholder="All Branches" /></div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {(branches || []).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <ShareViewButton params={{}} />
            <Select value={packPeriod} onValueChange={setPackPeriod}>
              <SelectTrigger className="w-36 h-9" data-testid="tie-out-period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="this_month">This Month</SelectItem>
                <SelectItem value="last_month">Last Month</SelectItem>
                <SelectItem value="this_year">This Year</SelectItem>
                <SelectItem value="all">All Time</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={downloadTieOutPack} disabled={packLoading} className="h-9 gap-1.5" data-testid="download-tie-out-pack-btn">
              {packLoading ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />}
              Download Tie-Out Pack
            </Button>
            <PageTour storageKey="tour_reports" steps={[
              { target: '[data-testid="report-card-analytics"]', title: 'Report Categories', content: 'Reports are organized by type. Click any card to open that specific report.' },
              { target: '[data-testid="reports-hub-title-2"]', title: 'Reports Hub', content: 'Central hub for all business reports including financial, operational, and compliance reports.' },
            ]} />
          </div>
          <p className="text-muted-foreground text-sm mt-1">Access all your reports and analytics from one place</p>
        </div>

        {reportSections.map((section) => {
          const colors = colorMap[section.color];
          return (
            <div key={section.title}>
              <div className="flex items-center gap-2 mb-3">
                <div className={`w-1.5 h-5 rounded-full ${colors.header}`} />
                <h2 className={`text-lg font-semibold font-outfit ${colors.title}`}>{section.titleKey ? t(section.titleKey) : section.title}</h2>
                <Badge variant="outline" className="text-[10px]">{section.items.length}</Badge>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Card
                      key={item.path}
                      className={`cursor-pointer transition-all duration-200 border ${colors.bg} hover:shadow-md hover:-translate-y-0.5`}
                      onClick={() => openReport(item.path)}
                      data-testid={`report-card-${item.path.replace('/', '')}`}
                    >
                      <CardContent className="p-4 flex items-start gap-3">
                        <div className={`p-2 rounded-lg bg-white/70 ${colors.icon}`}>
                          <Icon size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{item.label}</span>
                            {item.badge && (
                              <Badge className="text-[9px] px-1.5 py-0 h-4 bg-primary/10 text-primary border-0">{item.badge}</Badge>
                            )}
                          </div>
                          <p className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{item.desc}</p>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </DashboardLayout>
  );
}
